import { useCallback, useEffect } from "react";
import { shareResult } from "../share.ts";
import { useStore } from "../store.ts";
import { formatSyncCode, makeSyncCode, parseSyncCode, syncLink } from "./code.ts";
import { syncEngine, useSyncStatus, type SyncState } from "./engine.ts";

export interface SyncControls {
  state: SyncState;
  error: string | null;
  code: string | null;
  codeFormatted: string | null;
  lastSyncedAt: number | null;
  /** Start a new group from this device's progress. Resolves to the new code. */
  createCode: () => Promise<string>;
  /** Join an existing group. Rejects with a readable message if the code is malformed or the join fails. */
  join: (typed: string) => Promise<void>;
  /** Share the link (share sheet, else clipboard). */
  shareLink: () => Promise<"shared" | "copied" | "cancelled" | "failed">;
  copyCode: () => Promise<boolean>;
  syncNow: () => Promise<void>;
  disconnect: () => void;
  deleteRemote: () => Promise<void>;
}

export function useSync(): SyncControls {
  const { state, error } = useSyncStatus();
  const sync = useStore((s) => s.sync);

  const createCode = useCallback(async () => {
    const code = makeSyncCode();
    await syncEngine.connect(code);
    return code;
  }, []);
  const join = useCallback(async (typed: string) => {
    const code = parseSyncCode(typed);
    if (!code) throw new Error("That doesn't look like a sync code. It has 16 letters and numbers, like ABCD-EFGH-JKMN-PQRS.");
    await syncEngine.connect(code);
  }, []);
  const shareLink = useCallback(async () => {
    const code = useStore.getState().sync.code;
    if (!code) return "failed" as const;
    return shareResult(`Open this on your other device to sync Pass the Verse: ${syncLink(window.location.origin, code)}`);
  }, []);
  const copyCode = useCallback(async () => {
    const code = useStore.getState().sync.code;
    if (!code) return false;
    try {
      await navigator.clipboard.writeText(formatSyncCode(code));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    state,
    error,
    code: sync.code,
    codeFormatted: sync.code ? formatSyncCode(sync.code) : null,
    lastSyncedAt: sync.lastSyncedAt,
    createCode,
    join,
    shareLink,
    copyCode,
    syncNow: () => syncEngine.pull(),
    disconnect: () => syncEngine.disconnect(),
    deleteRemote: () => syncEngine.deleteRemote(),
  };
}

const RECHECK_MS = 60_000;

/** Mounted once. Watches for local changes to push, and pulls at launch, on return to the foreground, and when back online. */
export function useSyncLifecycle(): void {
  const code = useStore((s) => s.sync.code);
  useEffect(() => {
    const stop = syncEngine.start();
    if (!code) return stop;
    const pull = () => void syncEngine.pull().catch(() => undefined);
    const first = setTimeout(pull, 1200);
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < RECHECK_MS) return;
      last = Date.now();
      pull();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", pull);
    return () => {
      stop();
      clearTimeout(first);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", pull);
    };
  }, [code]);
}

/** "just now", "4 min ago", "2 hr ago", or a short date. */
export function timeAgo(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} hr ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
